import React, { useContext, useEffect, useMemo, useState } from "react";
import {
    Container,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableSortLabel,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
} from "@mui/material";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { publicApi } from "../../api/http";
import { DraftYearContext } from "../../contexts/DraftYearContext";
import PageLoader from "../../components/PageLoader/PageLoader";
import { DEFAULT_PLAYER_IMAGE } from "../../components/DraftOdds/DraftOddsComponents";
import "./ArrivalRankings.css";

const STAGE_ORDER = ["M0", "M1", "M2", "M3", "M4", "M5"];
const STAGE_LABELS = {
    M0: "Draft year",
    M1: "+1 year",
    M2: "+2 years",
    M3: "+3 years",
    M4: "+4 years",
    M5: "+5 years",
};
const METRICS = {
    dy0: { key: "dy0", horizon: 0, short: "This season" },
    dy1: { key: "dy1", horizon: 1, short: "Next season" },
    dy2: { key: "dy2", horizon: 2, short: "+2 seasons" },
    dy3: { key: "dy3", horizon: 3, short: "+3 seasons" },
    p_nhler: { key: "p_nhler", horizon: null, short: "Any of next 4" },
};

function parseBoard(data) {
    if (typeof data === "string") {
        try {
            return JSON.parse(data);
        } catch {
            return { rows: [], stages: [], available_years: [] };
        }
    }
    return data && typeof data === "object"
        ? data
        : { rows: [], stages: [], available_years: [] };
}

function seasonLabel(year) {
    if (year == null || Number.isNaN(Number(year))) return "—";
    const start = Number(year);
    return `${start}–${String(start + 1).slice(-2)}`;
}

function formatPct(value) {
    if (value == null || Number.isNaN(Number(value))) return "—";
    return `${(Number(value) * 100).toFixed(1)}%`;
}

function playerName(row) {
    return `${row.first_name || ""} ${row.last_name || ""}`.trim() || row.player_id;
}

function compareNullableNumber(a, b, ascending) {
    const aMissing = a == null || Number.isNaN(Number(a));
    const bMissing = b == null || Number.isNaN(Number(b));
    if (aMissing && bMissing) return 0;
    if (aMissing) return 1;
    if (bMissing) return -1;
    const diff = Number(a) - Number(b);
    return ascending ? diff : -diff;
}

function ArrivalRankings() {
    const { draftYear } = useContext(DraftYearContext);
    const [board, setBoard] = useState({ rows: [], stages: [], available_years: [] });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [stage, setStage] = useState(null);
    const [metric, setMetric] = useState("dy0");
    const [orderBy, setOrderBy] = useState("dy0");
    const [order, setOrder] = useState("desc");

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        setError(null);
        publicApi
            .get("/prospect_model_projections/board", { params: { draft_year: draftYear } })
            .then((response) => {
                if (cancelled) return;
                const next = parseBoard(response.data);
                setBoard(next);
                const stages = STAGE_ORDER.filter((kind) => next.stages.includes(kind));
                const current = next.rows.find((row) => row.is_dy0_stage)?.kind;
                setStage(current && stages.includes(current) ? current : stages[0] || null);
            })
            .catch((err) => {
                if (cancelled) return;
                console.error("Error fetching arrival rankings:", err);
                setError("Failed to load NHL arrival rankings.");
                setBoard({ rows: [], stages: [], available_years: [] });
                setStage(null);
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [draftYear]);

    const stages = useMemo(
        () => STAGE_ORDER.filter((kind) => board.stages.includes(kind)),
        [board.stages]
    );

    const stageRows = useMemo(
        () => board.rows.filter((row) => !stage || row.kind === stage),
        [board.rows, stage]
    );

    const rankedRows = useMemo(() => {
        const withRank = [...stageRows]
            .sort((left, right) => {
                const byMetric = compareNullableNumber(left[metric], right[metric], false);
                if (byMetric !== 0) return byMetric;
                const byBlend = compareNullableNumber(left.blend_rank, right.blend_rank, true);
                if (byBlend !== 0) return byBlend;
                return playerName(left).localeCompare(playerName(right));
            })
            .map((row, index) => ({ ...row, viewRank: index + 1 }));

        return [...withRank].sort((left, right) => {
            if (orderBy === "player") {
                const diff = playerName(left).localeCompare(playerName(right));
                return order === "asc" ? diff : -diff;
            }
            if (orderBy === "position") {
                const diff = (left.position || "").localeCompare(right.position || "");
                return order === "asc" ? diff : -diff;
            }
            if (orderBy === "blend_rank") {
                return compareNullableNumber(left.blend_rank, right.blend_rank, order === "asc");
            }
            return compareNullableNumber(left[orderBy], right[orderBy], order === "asc");
        });
    }, [stageRows, metric, orderBy, order]);

    const y0 = rankedRows[0]?.season_year_y0 ?? (stage ? draftYear + Number(stage.slice(1)) : draftYear);

    const handleSort = (column) => {
        if (METRICS[column]) {
            setMetric(column);
        }
        if (orderBy === column) {
            setOrder((current) => (current === "asc" ? "desc" : "asc"));
            return;
        }
        setOrderBy(column);
        setOrder(column === "player" || column === "position" || column === "blend_rank" ? "asc" : "desc");
    };

    const handleMetric = (_event, next) => {
        if (!next) return;
        setMetric(next);
        setOrderBy(next);
        setOrder("desc");
    };

    const handleStage = (_event, next) => {
        if (!next) return;
        setStage(next);
    };

    const sortLabel = (column) => (
        <TableSortLabel
            active={orderBy === column}
            direction={orderBy === column ? order : column === "blend_rank" || column === "player" ? "asc" : "desc"}
            onClick={() => handleSort(column)}
            className="arrival-rankings-sort"
        />
    );

    return (
        <>
            <Helmet>
                <title>{`${draftYear} NHL Arrival Rankings - Hockey Pipeline`}</title>
                <meta
                    name="description"
                    content={`See which ${draftYear} draft prospects have the highest probability of playing in the NHL, ranked by draft-stage arrival models.`}
                />
                <link rel="canonical" href="https://hockeypipeline.com/arrival-rankings" />
            </Helmet>
            <PageLoader loading={loading} />
            <Container maxWidth="xl" className="arrival-rankings-page">
                <section className="home-panel arrival-rankings-hero">
                    <Typography className="arrival-rankings-eyebrow" component="p">
                        {draftYear} draft class
                    </Typography>
                    <Typography variant="h4" component="h1" className="arrival-rankings-page-title">
                        NHL Arrival Rankings
                    </Typography>
                    <Typography className="arrival-rankings-intro">
                        Who has the highest probability of playing at least one NHL regular-season
                        game. Choose a model (M0 at draft, M1 a year later, and so on) and a season
                        to rank the class.
                    </Typography>
                </section>

                {stages.length > 0 && (
                    <section className="home-panel arrival-rankings-panel">
                        <div className="arrival-rankings-controls">
                            <div className="arrival-rankings-control">
                                <Typography className="arrival-rankings-control-label">
                                    Model
                                </Typography>
                                <ToggleButtonGroup
                                    exclusive
                                    value={stage}
                                    onChange={handleStage}
                                    size="small"
                                    className="arrival-rankings-toggles"
                                    aria-label="Draft-stage model"
                                >
                                    {stages.map((kind) => (
                                        <ToggleButton key={kind} value={kind}>
                                            {kind}
                                            <span className="arrival-rankings-toggle-hint">
                                                {STAGE_LABELS[kind]}
                                            </span>
                                        </ToggleButton>
                                    ))}
                                </ToggleButtonGroup>
                            </div>
                            <div className="arrival-rankings-control">
                                <Typography className="arrival-rankings-control-label">
                                    Rank by
                                </Typography>
                                <ToggleButtonGroup
                                    exclusive
                                    value={metric}
                                    onChange={handleMetric}
                                    size="small"
                                    className="arrival-rankings-toggles"
                                    aria-label="Season probability"
                                >
                                    {Object.values(METRICS).map((item) => (
                                        <ToggleButton key={item.key} value={item.key}>
                                            {item.horizon == null
                                                ? item.short
                                                : seasonLabel(y0 + item.horizon)}
                                        </ToggleButton>
                                    ))}
                                </ToggleButtonGroup>
                            </div>
                        </div>
                    </section>
                )}

                {error && (
                    <Typography className="arrival-rankings-empty">{error}</Typography>
                )}

                {!loading && !error && rankedRows.length === 0 && (
                    <Typography className="arrival-rankings-empty">
                        No NHL arrival projections for the {draftYear} draft class yet.
                        {board.available_years?.length
                            ? ` Rankings are available for ${board.available_years.join(", ")}.`
                            : ""}
                    </Typography>
                )}

                {rankedRows.length > 0 && (
                    <>
                        <Typography className="arrival-rankings-count">
                            {rankedRows.length} players · {stage} · ranked by{" "}
                            {metric === "p_nhler"
                                ? "any of the next 4 seasons"
                                : seasonLabel(y0 + (METRICS[metric]?.horizon || 0))}
                            {board.model_version ? ` · ${board.model_version}` : ""}
                        </Typography>
                        <TableContainer className="arrival-rankings-table-wrap">
                            <Table className="arrival-rankings-table" size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell className="arrival-rankings-sticky arrival-rankings-sticky--rank">
                                            #
                                        </TableCell>
                                        <TableCell className="arrival-rankings-sticky arrival-rankings-sticky--player">
                                            Player {sortLabel("player")}
                                        </TableCell>
                                        <TableCell>
                                            Pos {sortLabel("position")}
                                        </TableCell>
                                        <TableCell align="right">
                                            {seasonLabel(y0)} {sortLabel("dy0")}
                                        </TableCell>
                                        <TableCell align="right">
                                            {seasonLabel(y0 + 1)} {sortLabel("dy1")}
                                        </TableCell>
                                        <TableCell align="right">
                                            {seasonLabel(y0 + 2)} {sortLabel("dy2")}
                                        </TableCell>
                                        <TableCell align="right">
                                            {seasonLabel(y0 + 3)} {sortLabel("dy3")}
                                        </TableCell>
                                        <TableCell align="right">
                                            Any of 4 {sortLabel("p_nhler")}
                                        </TableCell>
                                        <TableCell align="right">
                                            Class rank {sortLabel("blend_rank")}
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {rankedRows.map((row) => (
                                        <TableRow
                                            key={`${row.player_id}-${row.kind}`}
                                            className="arrival-rankings-row"
                                        >
                                            <TableCell className="arrival-rankings-rank arrival-rankings-sticky arrival-rankings-sticky--rank">
                                                {row.viewRank}
                                            </TableCell>
                                            <TableCell className="arrival-rankings-sticky arrival-rankings-sticky--player">
                                                <Link
                                                    to={`/player/${row.player_id}?view=post-draft`}
                                                    className="arrival-rankings-player"
                                                >
                                                    <img
                                                        src={row.player_image_url || DEFAULT_PLAYER_IMAGE}
                                                        alt=""
                                                        className="arrival-rankings-player-img"
                                                    />
                                                    <span>{playerName(row)}</span>
                                                </Link>
                                            </TableCell>
                                            <TableCell>{row.position || "—"}</TableCell>
                                            <TableCell
                                                align="right"
                                                className={metric === "dy0" ? "arrival-rankings-sorted" : undefined}
                                            >
                                                {formatPct(row.dy0)}
                                            </TableCell>
                                            <TableCell
                                                align="right"
                                                className={metric === "dy1" ? "arrival-rankings-sorted" : undefined}
                                            >
                                                {formatPct(row.dy1)}
                                            </TableCell>
                                            <TableCell
                                                align="right"
                                                className={metric === "dy2" ? "arrival-rankings-sorted" : undefined}
                                            >
                                                {formatPct(row.dy2)}
                                            </TableCell>
                                            <TableCell
                                                align="right"
                                                className={metric === "dy3" ? "arrival-rankings-sorted" : undefined}
                                            >
                                                {formatPct(row.dy3)}
                                            </TableCell>
                                            <TableCell
                                                align="right"
                                                className={metric === "p_nhler" ? "arrival-rankings-sorted" : undefined}
                                            >
                                                {formatPct(row.p_nhler)}
                                            </TableCell>
                                            <TableCell align="right">
                                                {row.blend_rank != null ? `#${row.blend_rank}` : "—"}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </>
                )}
            </Container>
        </>
    );
}

export default ArrivalRankings;
